import { useMutation } from '@apollo/client';
import React, { useState } from 'react';
import { match } from 'ts-pattern';

import { useCompleteFragment } from '@eluve/apollo-client';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Switch,
  useToast,
} from '@eluve/components';

import {
  setSiteBannerStatusMutation,
  siteBannerStatusFragment,
} from './operations';

export interface SiteBannerStatusToggleProps {
  siteBannerId: string;
}

export const SiteBannerStatusToggle: React.FC<SiteBannerStatusToggleProps> = ({
  siteBannerId,
}) => {
  const toast = useToast();
  const [nextPendingState, setNextPendingState] = useState<boolean | undefined>(
    undefined,
  );

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);

  const [updateSiteBannerStatus] = useMutation(setSiteBannerStatusMutation, {
    onError: () => toast.error('Failed to update site banner status'),
    optimisticResponse: (data) => ({
      updateSiteBannersByPk: {
        __typename: 'SiteBanners' as const,
        id: siteBannerId,
        isActive: data.isActive,
      },
    }),
  });

  const handleSave = () => {
    if (nextPendingState === undefined) {
      return;
    }

    updateSiteBannerStatus({
      variables: {
        siteBannerId,
        isActive: !nextPendingState,
      },
    });

    setIsConfirmationDialogOpen(false);
    setNextPendingState(undefined);
  };

  const siteBanner = useCompleteFragment({
    fragment: siteBannerStatusFragment,
    key: {
      id: siteBannerId,
    },
  });

  const cancelStatusChange = () => {
    setIsConfirmationDialogOpen(false);
    setNextPendingState(undefined);
  };

  return (
    <>
      <Dialog
        open={isConfirmationDialogOpen}
        onOpenChange={(open) => {
          if (open === false) {
            cancelStatusChange();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {match(nextPendingState)
                .with(true, () => 'Disable Site Banner')
                .otherwise(() => 'Enable Site Banner')}
            </DialogTitle>
            <DialogDescription>
              {match(nextPendingState)
                .with(
                  true,
                  () => 'Users will no longer be able to see this site banner.',
                )
                .otherwise(
                  () => 'Site banner will be shown to logged in users.',
                )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={cancelStatusChange}>
              Dismiss
            </Button>
            <Button onClick={handleSave}>Okay</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Switch
        onCheckedChange={(checked) => {
          setNextPendingState(!checked);
          setIsConfirmationDialogOpen(true);
        }}
        checked={Boolean(siteBanner?.isActive)}
      />
    </>
  );
};
